import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { workOrdersState } from '../jotai/atoms';
import Swal from 'sweetalert2';
import axiosInstance from '../api/axiosInstance';
import '../App.css';
import MultiSelectSupervisor from './MultiSelectSupervisor';
import './ScheduleEntryDetailModal.css'; // Import the new CSS file

function ScheduleEntryDetailModal({ isOpen, onClose, scheduleEntry, onUpdateScheduleEntry, onDeleteScheduleEntry, supervisors, isEditMode, assignedEntries, user, fetchScheduleEntries }) {
  // Determine if scheduleEntry is an array (merged event) or a single object
  const isMergedEvent = Array.isArray(scheduleEntry);
  const displayEntry = isMergedEvent ? scheduleEntry[0] : scheduleEntry; // Use the first entry for display/editing fields
  const navigate = useNavigate();
  const workOrderColumns = useAtomValue(workOrdersState);

  const [editedEntry, setEditedEntry] = useState(() => ({
    id: displayEntry.id,
    scheduledDate: displayEntry.scheduledDate,
    assignedManagers: displayEntry.assignedManagers ? displayEntry.assignedManagers.split(',') : [],
    assignedAssistants: displayEntry.assignedAssistants ? displayEntry.assignedAssistants.split(',') : [],
    taskId: displayEntry.task ? displayEntry.task.id : undefined,
    deploymentStatus: displayEntry.deploymentStatus || '배치계획',
    projectType: displayEntry.projectType || '재착공',
  }));

  useEffect(() => {
    const currentDisplayEntry = Array.isArray(scheduleEntry) ? scheduleEntry[0] : scheduleEntry;
    setEditedEntry({
      id: currentDisplayEntry.id,
      scheduledDate: currentDisplayEntry.scheduledDate,
      assignedManagers: currentDisplayEntry.assignedManagers ? currentDisplayEntry.assignedManagers.split(',') : [],
      assignedAssistants: currentDisplayEntry.assignedAssistants ? currentDisplayEntry.assignedAssistants.split(',') : [],
      taskId: currentDisplayEntry.task ? currentDisplayEntry.task.id : undefined,
      deploymentStatus: currentDisplayEntry.deploymentStatus || '배치계획',
      projectType: currentDisplayEntry.projectType || '재착공',
    });
  }, [scheduleEntry]);

  if (!isOpen) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    setEditedEntry({ ...editedEntry, [name]: newValue });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!editedEntry.assignedManagers || editedEntry.assignedManagers.length === 0) {
      alert('책임감리원은 필수 입력 항목입니다.');
      return;
    }
    // console.log('Edited entry before update:', editedEntry);
    onUpdateScheduleEntry(editedEntry); // deploymentStatus 포함하여 호출
  };

  const handleDelete = () => {
    // If it's a merged event, we need to decide how to delete.
    // For now, let's assume deleting the first entry means deleting the whole merged event.
    // Or, if the backend supports deleting by task and date range, that would be better.
    // Given the current backend, onDeleteScheduleEntry expects a single entry ID.
    // So, we'll pass the ID of the displayEntry.
    onDeleteScheduleEntry(displayEntry.id);
  };

  const handleCreateWorkOrder = async () => {
    const entriesToProcess = Array.isArray(scheduleEntry) ? scheduleEntry : [scheduleEntry];

    // 1. 중복 체크
    const allWorkOrderItems = Object.values(workOrderColumns).flatMap(column => column.items || []);
    const existingWorkOrderKeys = new Set(
      allWorkOrderItems
        .filter(wo => wo.type === '배치' && wo.details.assignments)
        .flatMap(wo => wo.details.assignments)
        .map(as => `${as.taskId}-${as.assignmentDate}`)
    );

    const newEntries = entriesToProcess.filter(entry => {
      const workOrderKey = `${entry.task.id}-${entry.scheduledDate}`;
      return !existingWorkOrderKeys.has(workOrderKey);
    });

    if (newEntries.length === 0) {
      Swal.fire('알림', '이미 모든 일정에 대한 작업지시가 존재합니다.', 'info');
      return;
    }

    // 2. API 페이로드 생성
    const firstEntry = newEntries[0];
    const sortedDates = newEntries.map(e => e.scheduledDate).sort();
    const startDate = sortedDates[0];
    const endDate = sortedDates[sortedDates.length - 1];
    const dateString = startDate === endDate ? startDate : `${startDate} ~ ${endDate}`;
    const workOrderTitle = `[${firstEntry.task.number}] ${firstEntry.task.name} ${dateString} 배치`;

    const details = {
      taskNumber: firstEntry.task.number,
      taskRemarks: firstEntry.task.remarks || '',
      assignments: newEntries.map(entry => ({
        assignmentDate: entry.scheduledDate,
        taskId: entry.task.id,
        taskName: entry.task.name,
        assignedManagers: entry.assignedManagers,
        assignedAssistants: entry.assignedAssistants,
        projectType: entry.projectType, // projectType 추가
      })),
    };

    // 3. API 호출
    try {
      const detailsJsonString = JSON.stringify(details);
      await axiosInstance.post('/work-orders', {
        title: workOrderTitle,
        type: '배치',
        details: detailsJsonString,
        status: 'todo',
      });

      // 4. ScheduleEntry 상태 업데이트
      for (const entry of newEntries) {
        try {
          await axiosInstance.put(`/schedule-entries/${entry.task.id}/update-deployment-status`, null, {
            params: {
              scheduledDate: entry.scheduledDate,
              newStatus: '배치지시'
            }
          });
        } catch (statusError) {
          // 개별 상태 업데이트 실패는 일단 콘솔에만 기록합니다.
          console.error(`Failed to update deployment status for entry ${entry.id}:`, statusError);
        }
      }

      Swal.fire('성공', '작업지시가 성공적으로 생성되었습니다.', 'success');
      onClose(); // 모달 닫기
      fetchScheduleEntries(); // 달력 데이터 새로고침
    } catch (error) {
      console.error('Error creating work order:', error);
      Swal.fire('오류', '작업 지시 생성 중 오류가 발생했습니다.', 'error');
    }
  };

  // Function to format date range
  const formatDateRange = (entries) => {
    if (!entries) return '';

    let firstDate, lastDate;

    if (Array.isArray(entries)) {
      if (entries.length === 0) return '';
      const sortedEntries = [...entries].sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate));
      firstDate = new Date(sortedEntries[0].scheduledDate);
      lastDate = new Date(sortedEntries[sortedEntries.length - 1].scheduledDate);
    } else { // It's a single scheduleEntry object
      firstDate = new Date(entries.scheduledDate);
      lastDate = new Date(entries.scheduledDate);
    }

    const format = (date) => {
      const year = date.getFullYear().toString().slice(-2);
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      return `${year}.${month}.${day}`;
    };

    if (firstDate.getTime() === lastDate.getTime()) {
      return format(firstDate);
    } else {
      return `${format(firstDate)} ~ ${format(lastDate)}`;
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="modal-close-button">×</button>
        <h2>감리원 배치 {isEditMode ? '수정' : '상세 정보'}</h2>
        {displayEntry.task && displayEntry.task.branch && (
          <h3 className="modal-subtitle">{`${displayEntry.task.branch}`}</h3>
        )}
        <br/>
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label>공사 번호(감리)</label>
              <input type="text" value={displayEntry.task.number} readOnly={true} />
            </div>
            <div className="form-group">
              <label>공사번호(본공사)</label>
              <input type="text" value={displayEntry.task.mainTaskNumber || ''} readOnly={true} />
            </div>
            <div className="form-group">
              <label>공사명</label>
              <input type="text" value={displayEntry.task.name} readOnly={true} />
            </div>
            <div className="form-group">
              <label>공사 현황</label>
              <select name="projectType" value={editedEntry.projectType} onChange={handleInputChange} disabled={!isEditMode}>
                <option value="재착공">재착공</option>
                <option value="준공">준공</option>
              </select>
            </div>
            <div className="form-group">
              <label>일정 날짜</label>
              <input type="text" value={formatDateRange(scheduleEntry)} readOnly={true} /> {/* Use formatDateRange */}
            </div>
            <div className="form-group">
              <label>배정 책임감리</label>
              <MultiSelectSupervisor
                supervisors={supervisors}
                selectedSupervisors={editedEntry.assignedManagers}
                onSelectionChange={(values) => setEditedEntry({ ...editedEntry, assignedManagers: values })}
                placeholder="책임감리원을 선택하세요"
                disabled={!isEditMode}
                isManager={true}
                otherSelectedSupervisors={editedEntry.assignedAssistants}
                duplicateCheckEntries={assignedEntries}
              />
            </div>
            <div className="form-group">
              <label>배정 보조감리</label>
              <MultiSelectSupervisor
                supervisors={supervisors}
                selectedSupervisors={editedEntry.assignedAssistants}
                onSelectionChange={(values) => setEditedEntry({ ...editedEntry, assignedAssistants: values })}
                placeholder="보조감리원을 선택하세요"
                disabled={!isEditMode}
                isManager={false}
                otherSelectedSupervisors={editedEntry.assignedManagers}
                duplicateCheckEntries={assignedEntries}
              />
            </div>
            {/* 배치상태 드롭다운 추가 */}
            <div className="form-group">
              <label>배치 상태</label>
              <select name="deploymentStatus" value={editedEntry.deploymentStatus} onChange={handleInputChange} disabled={true}>
                <option value="배치계획">배치계획</option>
                <option value="배치지시">배치지시</option>
                <option value="배치완료">배치완료</option>
              </select>
            </div>
          </div>
          <div className="form-group form-group-full-width">
            <label>비고</label>
            <textarea value={displayEntry.task.remarks || ''} readOnly={true} rows="3"></textarea>
          </div>
          <div className="form-actions schedule-detail-actions-container"> {/* Apply new container class */}
            {isEditMode && (
              <>
                <button type="submit" className="schedule-detail-edit-button">수정하기</button>
                <button type="button" onClick={handleDelete} className="schedule-detail-delete-button">삭제하기</button>
              </>
            )}
            {!isEditMode && user && user.role === 'ADMIN' && displayEntry.deploymentStatus === '배치계획' && isMergedEvent && (
              <button type="button" onClick={handleCreateWorkOrder} className="schedule-detail-create-work-order-button">작업지시 생성</button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default ScheduleEntryDetailModal;
